import { useCallback } from 'react';
import { func, bool, shape, number, string } from 'prop-types';
import { FormattedMessage, defineMessages } from '@alltrails/shared/react-intl';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import Menu from '@alltrails/shared/icons/Menu';
import { providedPropTypes } from '../../../proptypes/shared';
import OriginalLanguageButton from '../../shared/OriginalLanguageButton';
import MenuPop from '../../shared/MenuPop';
import * as styles from './styles/styles.module.scss';
import CustomProvider from 'components/CustomProvider';

const WAYPOINT_CARD_STRINGS = defineMessages({
  DELETE: { defaultMessage: 'Delete' },
  EDIT: { defaultMessage: 'Edit' },
  SORT: { defaultMessage: 'Sort' }
});

const WaypointCard = ({
  id,
  idx,
  name,
  name_source,
  name_unsupported_language,
  description,
  description_source,
  description_unsupported_language,
  location,
  editWaypoint,
  deleteWaypoint,
  toggleSortMode,
  totalWaypointsLength,
  sortMode,
  provided,
  persistWaypointOrder,
  canEdit,
  isAdmin,
  handleMouseOver,
  toggleLanguageOrigin,
  showInTranslatedLang,
  enable_translations
}) => {
  const {
    formattedDefaultMessages: { DELETE, EDIT, SORT }
  } = useFormatMessage(WAYPOINT_CARD_STRINGS);
  const { latitude, longitude } = location;
  let renderMenuPop;
  let renderToggleLanguageButton;
  let toggleShowInTranslatedLang;

  if (canEdit) {
    const menuOptions = [
      {
        label: DELETE,
        callback: useCallback(() => {
          deleteWaypoint(id);
        }, [idx])
      }
    ];

    // Cannot edit in-place if not one of our supported languages: offer Edit Translations link to admins for those
    if (!enable_translations || (!name_unsupported_language && !description_unsupported_language)) {
      menuOptions.unshift({
        label: EDIT,
        callback: useCallback(() => {
          editWaypoint(idx);
        }, [idx])
      });
    } else if (isAdmin) {
      menuOptions.unshift({
        label: 'Edit Translations',
        callback: useCallback(() => {
          const href = `/edit-translations?table=at_map_markers&column_name=name&id=${id}`;
          window.open(href, '_blank');
        }, [idx])
      });
    }

    if (totalWaypointsLength > 1) {
      menuOptions.push({
        label: SORT,
        callback: toggleSortMode
      });
    }
    renderMenuPop = <MenuPop items={menuOptions} />;
  }

  if (name_source || description_source) {
    toggleShowInTranslatedLang = () => {
      toggleLanguageOrigin(idx, showInTranslatedLang);
    };

    renderToggleLanguageButton = <OriginalLanguageButton active={showInTranslatedLang} handleClick={toggleShowInTranslatedLang} />;
  }

  const nameText = !showInTranslatedLang && name_source ? name_source : name;
  const descriptionText = !showInTranslatedLang && description_source ? description_source : description;

  return (
    <CustomProvider>
      <div className={styles.container} onMouseEnter={() => handleMouseOver(id, location)}>
        <div className={styles.topSection}>
          <div className={styles.numberAndName}>
            {provided && canEdit && (
              <span className={styles.dragHandle} {...provided.dragHandleProps} title="Reorder">
                <Menu size="sm" />
              </span>
            )}
            <div className={styles.orderNumber}>{idx + 1}</div>
            <div className={styles.nameText}>{nameText}</div>
          </div>
          {sortMode ? (
            <button type="button" className={styles.doneButton} onClick={persistWaypointOrder}>
              <FormattedMessage defaultMessage="Done" />
            </button>
          ) : (
            renderMenuPop
          )}
        </div>
        <div className={styles.descriptionText}>{descriptionText}</div>
        <div className={styles.coordinatesAndLanguageBtn}>
          <div className={styles.latLng}>
            {latitude}, {longitude}
          </div>
          {renderToggleLanguageButton}
        </div>
      </div>
    </CustomProvider>
  );
};

WaypointCard.propTypes = {
  id: number,
  idx: number.isRequired,
  name: string,
  description: string,
  location: shape({
    latitude: number,
    longitude: number
  }),
  editWaypoint: func,
  deleteWaypoint: func,
  toggleSortMode: func,
  totalWaypointsLength: number,
  sortMode: bool,
  provided: providedPropTypes,
  persistWaypointOrder: func,
  canEdit: bool,
  isAdmin: bool,
  handleMouseOver: func,
  totalWaypointsNumber: number,
  showInTranslatedLang: bool,
  name_source: string,
  name_unsupported_language: bool,
  description_source: string,
  description_unsupported_language: bool,
  toggleLanguageOrigin: func,
  enable_translations: bool
};

WaypointCard.defaultProps = {
  provided: null,
  handleMouseOver: () => {},
  id: null,
  name: '',
  description: '',
  location: {
    latitude: null,
    longitude: null
  },
  editWaypoint: () => {},
  deleteWaypoint: () => {},
  toggleSortMode: () => {},
  totalWaypointsNumber: 1,
  sortMode: bool,
  persistWaypointOrder: () => {},
  canEdit: false,
  isAdmin: false,
  totalWaypointsLength: 1,
  showInTranslatedLang: false,
  name_source: null,
  name_unsupported_language: false,
  description_source: null,
  description_unsupported_language: false,
  toggleLanguageOrigin: () => {},
  enable_translations: false
};

export default WaypointCard;
